// Global Instructions Rule Applied!

import { z } from 'zod'

// Shape of one cfg.document_report_parameters row. These drive the parameter
// controls on the Reports screen: each row becomes one InputManager FieldConfig,
// with input_type mapping onto FieldTypeEnum.
export const DocumentReportParameterModelSchema = z
  .object({
    id: z.string(),
    param_key: z.string(),
    label: z.string(),
    input_type: z.enum(['select', 'date', 'checkbox', 'text', 'number']),
    // Where a select's options come from: a globalConfigs collection
    // ('funds', 'fund_groups'), or 'static' meaning options_json holds them.
    options_source: z.string().nullish(),
    options_json: z.array(z.object({ value: z.string(), label: z.string() })).default([]),
    is_multiple: z.boolean(),
    is_required: z.boolean(),
    // A literal, or a token the screen resolves (e.g. '{last_quarter_end}').
    default_value: z.string().nullish(),
    sequence: z.number(),
    // Conditional visibility: this control only appears when the named sibling
    // parameter holds the given value. A hidden control is never required.
    visible_when_param: z.string().nullish(),
    visible_when_value: z.string().nullish()
  })
  .strict()

// Shape of one cfg.document_report_sections row, nested under its report: a
// block the report prints, its order, and the company-count band it applies
// to (the single-company / SPV layout of the quarterly report). Read by the
// backend builders only; present so the registry stays inspectable.
export const DocumentReportSectionModelSchema = z
  .object({
    id: z.string(),
    section_key: z.string(),
    title: z.string(),
    sequence: z.number(),
    companies_min: z.number(),
    // null = no upper bound.
    companies_max: z.number().nullish(),
    settings: z.record(z.string(), z.unknown()).default({}),
    is_active: z.boolean()
  })
  .strict()

// Shape of one cfg.document_reports row as it appears in
// get_global_configs_v3 -> 'document_reports', with its parameters nested.
//
// template_name and data_builder_key name backend code artifacts; they are
// present so the registry stays inspectable from the client, not because the
// frontend dispatches on them — the backend owns rendering entirely.
export const DocumentReportModelSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    description: z.string(),
    menu_item_id: z.string(),
    template_name: z.string(),
    data_builder_key: z.string(),
    // 'slide' is the 16:9 presentation sheet (13.333in x 7.5in when paired
    // with orientation 'landscape') used by deck-style reports.
    page_size: z.enum(['letter', 'a4', 'legal', 'slide']),
    orientation: z.enum(['portrait', 'landscape']),
    margin_css: z.string(),
    filename_pattern: z.string(),
    // Names the parameter Download fans out over: when that parameter holds
    // several values the screen generates one report per value and delivers
    // them as a zip. Null means the report is single-shot.
    batch_param_key: z.string().nullish(),
    sequence: z.number(),
    is_active: z.boolean(),
    // Whether the Reports screen offers the Customize drawer (stored edits
    // in public.document_report_customizations).
    supports_customization: z.boolean(),
    // Free-form per-report settings (cfg.document_reports.settings), read by
    // the backend builders only — e.g. the Key Drivers realization weight.
    settings: z.record(z.string(), z.unknown()).default({}),
    parameters: z.array(DocumentReportParameterModelSchema).default([]),
    sections: z.array(DocumentReportSectionModelSchema).default([])
  })
  .strict()

export type DocumentReportSectionModel = z.infer<typeof DocumentReportSectionModelSchema>
export type DocumentReportParameterModel = z.infer<typeof DocumentReportParameterModelSchema>
export type DocumentReportModel = z.infer<typeof DocumentReportModelSchema>
